import { Box, IconButton, Switch, Tooltip, Typography, Button, Grid, Chip, Menu, MenuItem } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useParams } from 'react-router';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { FlowBuilderActions } from '../../../Services/_actions/index.js';
import { useSnackBar } from '../Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValue } from 'recoil';
import { botFlowDataAtom, userInformationAtom } from '../../../Services/_states/index.js';
import CommonTable from '../../Shared/Tables/TableV1.tsx'
import Pagination from '../Common/Pagination/index.tsx'
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmBox from '../Common/ConfirmBox/index.tsx'
import LongStringView from '../../Shared/LongStringView/index.tsx';
const SchedulerModal = React.lazy(() => import('./SchedulerModal.tsx'));
/**
 * @typedef {Object} ScheduledMessageColumn
 * @property {number} index
 * @property {string} id - Actual ID from backend
 * @property {string} name - Campaign/Message Name
 * @property {string} channel
 * @property {string} status
 * @property {string} scheduled_at
 * @property {string} updated_at
 */

const ScheduleMessagesList = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [schedulerModalOpen, setSchedulerModalOpen] = useState(false);
  const [activeScheduler, setActiveScheduler] = useState(null);
  const [botFlowData, setBotFlowData] = useRecoilState < any > (botFlowDataAtom);
  const actions = FlowBuilderActions();
  const [loading, setLoading] = useState(false);
  const { flowId } = useParams();
  const [tableState, setTableState] = useState < any > ({ filters: { search: "", page: 1, limit: 10, flowId: flowId } });
  const breadcrumbsData = [
    { label: <IconButton color='primary'><HomeIcon /></IconButton>, href: "/" },
    { label: 'Flow List', href: '/r/flow/list' },
    { label: 'Flow Builder', href: `/r/flow/builder/${flowId}` },
    { label: 'Scheduled Messages' },
  ];
  const [openConfirmBox, setOpenConfirmBox] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const initialNewSchedulerData = useMemo(() => ({
    message: {},
    schedule_type: '',
    node_type: '',
  }), []);

  const defaultModalData = useMemo(() => {
    return activeScheduler ? { ...initialNewSchedulerData, ...activeScheduler, message: { ...(initialNewSchedulerData.message || {}), ...(activeScheduler.message || {}) } } : initialNewSchedulerData;
  }, [activeScheduler, initialNewSchedulerData]);

  const generateFilterKey = (filters) => JSON.stringify(filters);

  const fetchScheduledMessages = async (params) => {
    try {
      const res = await actions.scheduleBot.get(params);
      const key = generateFilterKey(params);

      setTableState((prev) => ({
        ...prev,
        cache: {
          ...prev.cache,
          [key]: res.data || [],
        },
        totalCountCache: {
          ...prev.totalCountCache,
          [key]: res.data?.length || 0,
        },
        filters: params,
      }));
    } catch (error) {
      console.error('Error fetching scheduled messages:', error);
    }
  };

  const handleDelete = async (item) => {
    if (!item || !item.scheduler_id) {
      showSnackBar("Missing scheduler ID for deletion", "error");
      return;
    }
    setConfirmAction({ action: 'delete', item });
    setOpenConfirmBox(true);
  };
  const handleConfirmClose = async (confirmed) => {
    if (confirmed && confirmAction) {
      // if (confirmAction.action === 'update') {
      //   setActiveScheduler(confirmAction.item);
      // } else 
      if (confirmAction.action === 'delete') {
        try {
          const res = await actions.scheduleBot.delete({ scheduler_id: confirmAction.item.scheduler_id });
          if (res?.status === 200 || res?.status === 204) {
            showSnackBar("Schedule deleted", "success");
            setTableState((prevState) => {
              const updatedCache = Object.entries(prevState.cache).reduce((acc, [key, list]) => {
                const updatedList = list?.filter((item) => item?.scheduler_id !== confirmAction?.item?.scheduler_id);
                acc[key] = updatedList;
                return acc;
              }, {});
              return { ...prevState, cache: updatedCache };
            });
          } else {
            showSnackBar("Failed to delete", "error");
          }
        } catch (e) {
          console.error("Delete error:", e);
          showSnackBar("Error deleting", "error");
        }
      }
    } else {
      showSnackBar("Action cancelled", "info");
    }
    setOpenConfirmBox(false);
    setConfirmAction(null);
  };


  const fetchBotFlowDetails = async () => {
    let res = await actions.getBotFlowDetails(flowId);
    if (res?.status === 200) {
      setBotFlowData({ ...res?.data });
    }
  };

  useEffect(() => {
    if (flowId) {
      fetchBotFlowDetails();
    }
  }, []);



  const handleScheduleSave = async (scheduler) => {
    try {
      const payload = {
        flow_id: flowId,
        bot_number: botFlowData?.phone_number_id,
        company_id: botFlowData?.company_id,
        message: scheduler?.message ?? {},
        created_at: new Date()?.toISOString(),
        created_by: loggedInUser?.username ?? '',
        ...scheduler
      };

      const res = scheduler.scheduler_id
        ? await actions.scheduleBot.put({ ...payload, scheduler_id: scheduler.scheduler_id }) // If updating an existing schedule
        : await actions.scheduleBot.post(payload); // If creating a new schedule

      if (res?.status === 200 || res?.status === 201) {
        if (scheduler?.scheduler_id) {
          setTableState((prevState) => {
            const updatedCache = Object.entries(prevState.cache).reduce((acc, [key, list]) => {
              const updatedList = list?.map((item) =>
                item.scheduler_id === scheduler.scheduler_id ? {
                  ...payload,
                } : item
              );
              acc[key] = updatedList;
              return acc;
            }, {});

            return { ...prevState, cache: updatedCache };
          });
          showSnackBar("Schedule updated successfully", "success");
        } else {
          setTableState((prevState) => {
            const updatedCache = Object.entries(prevState.cache).reduce((acc, [key, list]) => {
              const updatedList = [{ ...payload, scheduler_id: res.data?.data?.scheduler_id ?? "" }, ...list]
              acc[key] = updatedList;
              return acc;
            }, {});
            return { ...prevState, cache: updatedCache };
          });
          showSnackBar("Schedule saved successfully", "success");
        }
      } else {
        showSnackBar("Failed to save schedule", "error");
      }

    } catch (e) {
      showSnackBar("Error scheduling message", "error");
    }
  };


  useEffect(() => {
    fetchScheduledMessages(tableState?.filters);
  }, []);

  const columns = [
    {
      accessorKey: 'node_id',
      header: 'Node ID',
      enableSorting: false,
    },
    {
      accessorKey: 'node_type',
      header: 'Type',
      enableSorting: false,
    },
    {
      accessorKey: 'schedule_type',
      header: 'Schedule Type',
      enableSorting: false,

    },
  {
  accessorKey: 'delay',
  header: 'Delay',
  enableSorting: false,
  Cell: ({ cell }) => {
    const value = cell.getValue();
    return value !== null && value !== undefined && value !== '' ? value : 'N/A';
  },},
    {
      accessorKey: 'message',
      header: 'Message',
      enableSorting: false,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return <LongStringView longString={value?.text ?? value?.body?.text ??'-'} />;
      },
    },
    {
      accessorKey: 'created_by',
      header: 'Created By',
      enableSorting: false,

    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      enableSorting: false,

      Cell: ({ cell }) => {
        const raw = cell.getValue();
        if (!raw) return '-';
        const date = new Date(raw);
        return date.toLocaleString('en-IN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
      },
    },
    {
      header: 'Actions',
      enableSorting: false,
      accessorKey: 'actions',
      size: 60,
      Cell: ({ row }) => {
        const item = row.original;
        const [anchorEl, setAnchorEl] = React.useState < null | HTMLElement > (null);
        const open = Boolean(anchorEl);

        const handleClick = (event) => {
          setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        return (
          <Box>
            {/* <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}> */}
            <IconButton size="small" onClick={handleClick}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  setSchedulerModalOpen(true);
                  setActiveScheduler(item);
                  // handleUpdate(menuRow);
                  handleClose();
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDelete(item);
                  handleClose();
                }}
                sx={{ color: 'red' }}
              >
                Delete
              </MenuItem>
            </Menu>
          </Box>
        );
      },
    }

  ];

  const handleChangePage = (newPage) => {
    fetchScheduledMessages({ ...tableState.filters, page: newPage });
  };

  const handleChangeRowsPerPage = (newLimit) => {
    fetchScheduledMessages({ ...tableState.filters, limit: newLimit, page: 1 });
  };




  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
      <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item) => {
        if (item) {
          navigate(item?.href);
        }
      }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', fontSize: { xs: "20px", sm: "2.125rem;" } }}>Scheduled Messages</Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Button variant="contained" color="primary" size="small" onClick={() => { setSchedulerModalOpen(true); setActiveScheduler({}) }}>
            Add
          </Button>
        </Grid>
      </Grid>
      <CommonTable
        columns={columns}
        data={tableState?.cache?.[generateFilterKey(tableState?.filters)] || []}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 347px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
          },
        }}
        initialState={{
          columnPinning: {
            right: ['actions'],
          },
        }}
        enableBottomToolbar={false}
      // renderBottomToolbar={() => (
      //   <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
      //     <Pagination
      //       totalCount={tableState?.totalCountCache?.[generateFilterKey(tableState?.filters)] || 0}
      //       rowsPerPage={tableState?.filters?.limit}
      //       page={tableState?.filters?.page}
      //       handleChangePage={handleChangePage}
      //       handleChangeRowsPerPage={handleChangeRowsPerPage}
      //     />
      //   </Box>
      // )}
      />

      {/* <CommonTable
                columns={columns}
                data={tableState?.cache[generateFilterKey(tableState?.filters)] || []}
                // data={fetchedData.state === 'hasValue' ? fetchedData.contents.data : []} // Alternative if cache is not immediately populated
                muiTableContainerProps={{
                    sx: {
                        height: 'calc(100vh - 347px)',
                        width: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        }
                    },
                }}
                renderBottomToolbar={() => (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
                        <Pagination
                            totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
                            rowsPerPage={tableState?.filters?.limit}
                            page={tableState?.filters?.page}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Box>
                )}
            /> */}
      <SchedulerModal
        open={schedulerModalOpen}
        onClose={() => setSchedulerModalOpen(false)}
        botFlowData={botFlowData}
        onSave={(data) => {
          handleScheduleSave(data);
          setActiveScheduler({});
        }}
        defaultData={defaultModalData}
        data={{
    ...defaultModalData,
    flowId,
    onSchedulerSave: () => fetchScheduledMessages(tableState?.filters) // 👈 this refreshes the list
  }}
      />

      <ConfirmBox
        open={openConfirmBox}
        onClose={handleConfirmClose}
        options={{
          title: confirmAction?.action === 'update' ? 'Are you sure to update?' : 'Are you sure to delete?',
          text: confirmAction?.action === 'update'
            ? 'Do you want to proceed with updating this schedule?'
            : 'Do you want to proceed with deleting this schedule?',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',

        }}
      />

    </Box>
  );
};

export default ScheduleMessagesList;
